import React, { useState, useEffect } from 'react';
import { Microscope } from 'lucide-react';

import Header from './components/Header';
import Landing from './components/Landing';
import Question from './components/Question';
import ResultPage from './components/ResultPage';
import BlogDetailPage from './components/BlogDetailPage';
import AboutPage from './components/AboutPage';

import { INITIAL_BLOG_POSTS, QUESTIONS, RESULTS, RSS2JSON_API } from './constants';
import { BlogPost, ResultType } from './types';

// --- Tracking Integration ---
const trackEvent = (eventName: string, params: Record<string, any> = {}) => {
  // 1. Google Analytics 4
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', eventName, params);
  }

  // 2. Meta Pixel (Facebook)
  if (typeof window !== 'undefined' && (window as any).fbq) {
    // Event Mapping for Ad Optimization
    if (eventName === 'quiz_complete') {
      // "Lead" is the standard event for form/quiz completions.
      // Use this as your "Conversion Goal" in Meta Ads Manager.
      (window as any).fbq('track', 'Lead', params);
    } else if (eventName === 'product_click') {
      // "ViewContent" indicates interest in a specific product.
      (window as any).fbq('track', 'ViewContent', {
        content_name: params.name,
        content_category: params.brand,
        currency: 'USD',
        value: 1.00 // Arbitrary value to track intent weight
      });
    } else {
      // Track other events (quiz_start, etc.) as Custom Events
      (window as any).fbq('trackCustom', eventName, params);
    }
  }
  
  // Console Log for Debugging
  console.log(`[Analytics] Event: ${eventName}`, params);
};

export default function AboobaQuiz() {
  const [step, setStep] = useState<string>('landing'); 
  const [currentQIdx, setCurrentQIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [resultData, setResultData] = useState<ResultType | null>(null);
  
  // Navigation States
  const [activeBlogPost, setActiveBlogPost] = useState<BlogPost | null>(null);
  const [showAbout, setShowAbout] = useState(false);

  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(INITIAL_BLOG_POSTS);

  // --- Medium Fetch Logic ---
  useEffect(() => {
    const fetchMediumPosts = async () => {
      try {
        const response = await fetch(`${RSS2JSON_API}&t=${Date.now()}`);
        if (!response.ok) throw new Error('API limit or network error');
        
        const data = await response.json();
        
        if (data.status === 'ok') {
          const formattedPosts = data.items?.map((item: any, index: number) => {
            let imageUrl = item.thumbnail;
            const contentStr = item.content || "";
            const descriptionStr = item.description || "";

            if (!imageUrl || !imageUrl.startsWith('http')) {
               const imgMatch = contentStr.match(/<img[^>]+src="([^">]+)"/);
               if (imgMatch) imageUrl = imgMatch[1];
            }
            
            if (!imageUrl) imageUrl = `https://picsum.photos/800/600?random=${index + 100}`;

            return {
              id: item.guid || `medium-${index}`,
              title: item.title,
              excerpt: descriptionStr.replace(/<[^>]+>/g, '').substring(0, 120) + "...", 
              date: item.pubDate ? item.pubDate.split(' ')[0] : new Date().toISOString().split('T')[0], 
              author: item.author,
              content: contentStr, 
              image: imageUrl,
              link: item.link
            };
          });
          
          if (formattedPosts && formattedPosts.length > 0) {
             setBlogPosts(formattedPosts);
          }
        }
      } catch (error) {
        console.warn("Failed to fetch Medium posts, using fallback data:", error);
      }
    };

    fetchMediumPosts();
  }, []);

  const startQuiz = () => {
    trackEvent('quiz_start');
    setStep('quiz');
    setActiveBlogPost(null);
    setShowAbout(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAnswer = (answer: string) => {
    const currentQ = QUESTIONS[currentQIdx];
    const newAnswers = { ...answers, [currentQ.id]: answer };
    setAnswers(newAnswers);

    trackEvent('quiz_question_answer', { 
      question_id: currentQ.id, 
      answer: answer 
    });

    if (currentQIdx < QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQIdx(currentQIdx + 1), 300); 
    } else {
      completeQuiz(newAnswers);
    }
  };

  const completeQuiz = (finalAnswers: Record<string, string>) => {
    let type = 'D'; 
    const concern = finalAnswers['concern'] || '';
    const age = finalAnswers['age'] || '';

    if (concern.includes('No major issues')) type = 'E';
    else if (concern.includes('Redness') || concern.includes('Itchiness')) type = 'C';
    else if (age === '0–6 months') type = 'A';
    else if (concern.includes('Dryness')) type = 'B';
    else type = 'D';

    trackEvent('quiz_complete', { 
      result_type: type,
      ...finalAnswers 
    });

    setStep('loading');
    setTimeout(() => {
      setResultData(RESULTS[type]);
      setStep('result');
    }, 2000);
  };

  const retakeQuiz = () => {
    trackEvent('quiz_retake');
    setAnswers({});
    setCurrentQIdx(0);
    setStep('landing');
    setResultData(null);
    setActiveBlogPost(null);
    setShowAbout(false);
  };

  const openBlogPost = (post: BlogPost) => {
    if (post) {
      // Enhanced Blog Tracking: Title, Author, ID
      trackEvent('blog_view', { 
        post_title: post.title, 
        post_id: post.id,
        post_author: post.author 
      });
      setActiveBlogPost(post);
      setShowAbout(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const closeBlogPost = () => {
    setActiveBlogPost(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogoClick = () => {
    setStep('landing');
    setCurrentQIdx(0);
    setAnswers({});
    setResultData(null);
    setActiveBlogPost(null);
    setShowAbout(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAboutClick = () => {
    trackEvent('about_page_view');
    setShowAbout(true);
    setActiveBlogPost(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeAbout = () => {
    setShowAbout(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollToBlog = () => {
    // Navigation Tracking
    trackEvent('nav_click', { section: 'blog' });

    if (activeBlogPost) setActiveBlogPost(null);
    if (showAbout) setShowAbout(false);
    
    if (step !== 'landing') {
        setStep('landing');
        setTimeout(() => {
            const blogSection = document.getElementById('blog-section');
            if (blogSection) {
                blogSection.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    } else {
        setTimeout(() => {
            const blogSection = document.getElementById('blog-section');
            if (blogSection) {
                blogSection.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    }
  };

  return (
    <div className="font-sans text-slate-900 antialiased selection:bg-orange-100 selection:text-orange-900">
      <Header 
        onBlogClick={handleScrollToBlog} 
        onLogoClick={handleLogoClick} 
        onAboutClick={handleAboutClick}
      />
      
      {showAbout ? (
        <AboutPage onBack={closeAbout} onStart={startQuiz} />
      ) : activeBlogPost ? (
        <BlogDetailPage 
          post={activeBlogPost} 
          onBack={closeBlogPost} 
          trackEvent={trackEvent}
        />
      ) : (
        <>
          {step === 'landing' && <Landing onStart={startQuiz} onBlogClick={openBlogPost} blogPosts={blogPosts} />}
          
          {step === 'quiz' && (
            <Question 
              data={QUESTIONS[currentQIdx]} 
              currentStep={currentQIdx} 
              totalSteps={QUESTIONS.length} 
              onAnswer={handleAnswer} 
            />
          )}
          
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center min-h-screen bg-white pt-14">
              <div className="relative w-24 h-24 mb-8">
                 <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                 <div className="absolute inset-0 border-4 border-orange-500 rounded-full border-t-transparent animate-spin"></div>
                 <Microscope className="absolute inset-0 m-auto text-slate-400" size={32} />
              </div>
              <h2 className="text-xl font-bold text-slate-900 tracking-tight uppercase">Analyzing Profile</h2>
              <p className="text-slate-500 text-sm mt-2 font-mono">Processing dermatological data...</p>
            </div>
          )}
          
          {step === 'result' && resultData && (
            <ResultPage result={resultData} onRetake={retakeQuiz} trackEvent={trackEvent} />
          )}
        </>
      )}
    </div>
  );
}